import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import {
  getAllRepoOwners,
  addRepoOwner,
  removeRepoOwner,
  updateRepoOwnerState
} from '../../store/slice/repoOwnerSlice';
import { getLoadingState } from '../../utils/Helper/helper';
import ConditionalLoader from '../../components/ConditionalLoader';
import Search from '../../components/Search';
import Button from '../../library/Button/button';
import { IPPHeading } from '../../library/Heading/Heading';
import Pagination from '../../library/Pagination/Pagination';
import RepoOwnerService from '../../utils/RepoOwner/RepoOwner';
import IRepoOwner from '../../utils/RepoOwner/RepoOwner.interface';
import IModuleList from '../../utils/ModuleList/ModuleList.interface';
import CreateOwnerPopup from '../../components/BtAdminPanel/RepoOwner/CreateOwnerPopup';
import EditOwnerPopup from '../../components/BtAdminPanel/RepoOwner/EditOwnerPopup';
import DeleteOwnerPopup from '../../components/BtAdminPanel/RepoOwner/DeleteOwnerPopup';
import AlertPopup from '../../components/BtAdminPanel/RepoOwner/AlertPopup';
import ViewModulesPopup from '../../components/BtAdminPanel/RepoOwner/ViewModulesPopup';
import { OwnerActionTooltip } from '../../components/FloatingToolTip/OwnerActionTooltip';
import ImageTag from '../../library/ImageTag/ImageTag';
import editIcon from '../../assets/img/edit.png';
import binIcon from '../../assets/img/bin.png';
import EyeOpenIcon from '../../library/Button/EyeOpenIcon';
import { renameModuleOwner } from '../../store/slice/moduleSlice';

const RepoOwners: React.FC = () => {
  const dispatch = useAppDispatch();
  const ownerState = useAppSelector((state) => state.repoOwners);
  const moduleState = useAppSelector((state) => state.modules);
  const owners = ownerState.data ?? [];
  const allModules: IModuleList[] = moduleState.data ?? [];
  const isLoading = getLoadingState(ownerState);

  const [query, setQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showViewModules, setShowViewModules] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState<IRepoOwner | null>(null);
  const [formData, setFormData] = useState<IRepoOwner>({ repoOwnerName: '', repoOwnerInfo: '' });

  const screenHeight = window.innerHeight;
  const itemsPerPage = screenHeight ? Math.ceil((screenHeight - 210) / 70) : 10;

  const sortedOwners = useMemo(() => [...owners].sort((a, b) =>
    (a.repoOwnerName ?? '').localeCompare(b.repoOwnerName ?? '')
  ), [owners]);

  const filteredOwners = useMemo(() => sortedOwners.filter(
    (o) =>
      (o.repoOwnerName ?? '').toLowerCase().includes(query.toLowerCase()) ||
      (o.repoOwnerInfo ?? '').toLowerCase().includes(query.toLowerCase())
  ), [sortedOwners, query]);

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentData = filteredOwners.slice(indexOfFirst, indexOfLast);

  const resetSearch = (newQuery: string) => {
    setQuery(newQuery);
    setCurrentPage(1);
  };

  const handleCreate = async () => {
    if (!formData.repoOwnerName.trim()) {
      setAlertMessage('Name is required');
      setShowAlert(true);
      return;
    }
    setSubmitting(true);
    const service = new RepoOwnerService();
    const result = await service.createRepoOwner(formData);
    if (result.statusCode === 201 || result.statusCode === 200) {
      dispatch(addRepoOwner({ ...formData, isActive: true }));
      setAlertMessage('Owner created successfully!');
      setShowCreatePopup(false);
      setFormData({ repoOwnerName: '', repoOwnerInfo: '' });
    } else {
      setAlertMessage(typeof result.body === 'string' ? result.body : 'Failed to create owner');
    }
    setShowAlert(true);
    setSubmitting(false);
  };

  const handleUpdate = async () => {
    if (!formData.repoOwnerName.trim() && !formData.repoOwnerInfo?.trim()) {
      setAlertMessage('Please update at least one field');
      setShowAlert(true);
      return;
    }
    setSubmitting(true);
    const service = new RepoOwnerService();
    const originalName = selectedOwner?.repoOwnerName ?? '';
    const payload: IRepoOwner = { ...formData, repoOwnerName: formData.repoOwnerName.trim() || originalName };
    const result = await service.updateRepoOwner(payload, originalName);
    if (result.statusCode === 200 || result.statusCode === 201) {
      dispatch(updateRepoOwnerState({ original: originalName, updated: { ...selectedOwner, ...payload } }));
      if (payload.repoOwnerName !== originalName) {
        dispatch(renameModuleOwner({ oldName: originalName, newName: payload.repoOwnerName }));
      }
      setAlertMessage('Owner updated successfully!');
      setShowEditPopup(false);
    } else {
      setAlertMessage(typeof result.body === 'string' ? result.body : 'Failed to update owner');
    }
    setShowAlert(true);
    setSubmitting(false);
  };

  const handleDelete = async () => {
    if (!selectedOwner) return;
    setSubmitting(true);
    setShowDeleteConfirm(false);
    const service = new RepoOwnerService();
    const result = await service.deleteRepoOwner(selectedOwner);
    if (result.statusCode === 200 || result.statusCode === 201) {
      dispatch(removeRepoOwner(selectedOwner));
      setAlertMessage('Owner deleted successfully!');
    } else {
      setAlertMessage(typeof result.body === 'string' ? result.body : 'Failed to delete owner');
    }
    setShowAlert(true);
    setSubmitting(false);
  };

  const openView = (owner: IRepoOwner) => {
    setSelectedOwner(owner);
    setShowViewModules(true);
  };

  const openEdit = (owner: IRepoOwner) => {
    setSelectedOwner(owner);
    setFormData({ ...owner });
    setShowEditPopup(true);
  };

  const openDelete = (owner: IRepoOwner) => {
    setSelectedOwner(owner);
    setShowDeleteConfirm(true);
  };

  useEffect(() => {
    if (ownerState.status !== 'loading' || !ownerState.data) {
      void dispatch(getAllRepoOwners());
    }
  }, []);

  return (
    <ConditionalLoader isLoading={isLoading} className="flex justify-center">
      <div className="w-full md:w-full">
        <div className="bg-white block md:w-[100%] mx-auto md:ps-0.5 md:pe-0.5">
          <div className="py-2 flex justify-between items-center gap-4">
            <Button
              className="text-[#fff] text-base cursor-pointer bg-black rounded-lg py-2 px-4 whitespace-nowrap"
              buttonText="+ Add Owner"
              onclick={() => {
                setFormData({ repoOwnerName: '', repoOwnerInfo: '' });
                setShowCreatePopup(true);
              }}
            />
            <Search
              onChange={(e: React.FormEvent) => resetSearch((e.target as HTMLInputElement).value)}
              text="Search owners..."
              hideLabel
            />
          </div>
        </div>

        {filteredOwners.length > 0 ? (
          <>
            <div className="w-full overflow-y-scroll mb-4">
              <div className="min-w-fit border rounded-md overflow-hidden">
                <table className="w-full whitespace-nowrap rounded-lg bg-white divide-y divide-gray-300">
                  <thead className="bg-[#f8f8f8]">
                    <tr className="text-black text-left">
                      <th className="font-semibold text-sm uppercase px-6 py-4">Owner Name</th>
                      <th className="font-semibold text-sm uppercase px-6 py-4">Owner Info</th>
                      <th className="font-semibold text-sm uppercase px-6 py-4 text-center">
                        No. of Repositories
                      </th>
                      <th className="font-semibold text-sm uppercase px-6 py-4 text-center">
                        Active
                      </th>
                      <th className="font-semibold text-sm uppercase px-6 py-4 text-center">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentData.map((owner) => (
                      <tr key={owner.repoOwnerName}>
                        <td className="px-6 py-4">{owner.repoOwnerName}</td>
                        <td className="px-6 py-4 text-gray-600">{owner.repoOwnerInfo || '-'}</td>
                        <td className="px-6 py-4 text-center">{owner.noOfRepository ?? 0}</td>
                        <td className="px-6 py-4 text-center">
                          <span
                            className={`inline-block w-3 h-3 rounded-full ${
                              owner.isActive ? 'bg-[#28a745]' : 'bg-[#e60014]'
                            }`}></span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center items-center gap-[2px]">
                            <OwnerActionTooltip
                              onClick={() => openView(owner)}
                              label="View Modules"
                              icon={<EyeOpenIcon />}
                            />
                            <br />
                            <OwnerActionTooltip
                              onClick={() => openEdit(owner)}
                              label="Edit Owner"
                              icon={<ImageTag src={editIcon} width="20px" alt="" name="Edit" />}
                            />
                            <OwnerActionTooltip
                              onClick={() => openDelete(owner)}
                              label="Delete Owner"
                              icon={<ImageTag src={binIcon} width="20px" alt="" name="Delete" />}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {filteredOwners.length > itemsPerPage && (
              <Pagination
                className="pagination-bar mt-4 flex justify-center items-center m-auto"
                currentPage={currentPage}
                totalCount={filteredOwners.length}
                pageSize={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        ) : (
          <div className="px-6 py-4 text-center pt-20 font-bold">
            <IPPHeading headerText="No owners found ☹️ !!" className="text-bold" />
          </div>
        )}

        {showViewModules && (
          <ViewModulesPopup
            show={showViewModules}
            onClose={() => setShowViewModules(false)}
            ownerName={selectedOwner?.repoOwnerName ?? ''}
            modules={allModules.filter((m) => m.owner === selectedOwner?.repoOwnerName)}
          />
        )}

        {showCreatePopup && (
          <CreateOwnerPopup
            show={showCreatePopup}
            onClose={() => setShowCreatePopup(false)}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleCreate}
            submitting={submitting}
          />
        )}

        {showEditPopup && (
          <EditOwnerPopup
            show={showEditPopup}
            onClose={() => setShowEditPopup(false)}
            formData={formData}
            originalData={selectedOwner!}
            setFormData={setFormData}
            onSubmit={handleUpdate}
            submitting={submitting}
          />
        )}

        {showDeleteConfirm && (
          <DeleteOwnerPopup
            show={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            ownerName={selectedOwner?.repoOwnerName ?? ''}
            assignedModulesCount={allModules.filter((m) => m.owner === selectedOwner?.repoOwnerName).length}
            onConfirm={handleDelete}
            submitting={submitting}
          />
        )}

        {showAlert && (
          <AlertPopup show={showAlert} onClose={() => setShowAlert(false)} message={alertMessage} />
        )}
      </div>
    </ConditionalLoader>
  );
};

export default RepoOwners;
